import {Card, Container, Group, Menu, rem, Stack, Text, Title, UnstyledButton} from "@mantine/core";
import {useTranslation} from "react-i18next";
import {
  ErrorPage,
  MonthPickerInput,
  PAGINATION_PAGE_SIZES,
  ResizeableContent,
  SettlementDataTable,
  ThemedPage,
  useApp,
  useAuth,
} from "@ots-frontend/ots-ui";
import {IconCalendar, IconChevronDown, IconGavel} from "@tabler/icons-react";
import {useAppSelector} from "../../store/hooks/useAppSelector";
import {getAppCategory} from "../../store/ui/selectors";
import {useCallback, useMemo} from "react";
import {
  addMonths,
  adjustToTimeZone,
  reportTimeType,
  SettlementSearchCriteria,
  settlementsSelector,
  useSearchSettlementsQuery
} from "@ots-frontend/ots-data";
import {getSettlementPageFilter} from "../../store/settlement-page/selectors";
import {useDispatch} from "react-redux";
import {setSettlementPageFilter} from "../../store/settlement-page/actions";

export type SettlementPageProps = {}

export const SettlementPage = (props: SettlementPageProps) => {
  const {t} = useTranslation(["translation", "common"]);

  const dispatch = useDispatch()
  const {regionId} = useApp()
  const {hasAccess} = useAuth()
  const appCategory = useAppSelector(getAppCategory);
  const filter = useAppSelector(getSettlementPageFilter)

  const criteria: SettlementSearchCriteria = useMemo(() => ({
    fromDttm: adjustToTimeZone(filter.dttm).getTime(),
    toDttm: adjustToTimeZone(addMonths(filter.dttm, 1)).getTime(),
    regionIds: regionId ? [regionId] : undefined,
    categories: appCategory ? [appCategory.code] : undefined,
    types: [reportTimeType.monthly.code],
  }), [filter, regionId, appCategory])

  const selectSettlements = useMemo(() => {
    return settlementsSelector();
  }, []);

  const {settlements, isFetching, refetch} = useSearchSettlementsQuery(criteria, {
    selectFromResult: (result) => ({
      ...result,
      settlements: selectSettlements(result),
      isFetching: result.isFetching,
    })
  });

  const onFilterChange = useCallback((date: Date) => {
    dispatch(setSettlementPageFilter({
      dttm: date.getTime(),
    }));
  }, [dispatch]);


  if (!hasAccess("settlement_view")) {
    return <ErrorPage/>
  }

  return (
    <ThemedPage>
      {(classes) => (
        <ResizeableContent>
          {(size) => (
            <Container
              w={size.width}
              h={size.height}
              className={classes.container}
              style={{"overflowY": "scroll"}}
              fluid={true}
              p="md"
            >
              <Stack h="100%" m="sm">
                <Card shadow="sm" padding="lg" mb="lg" radius="md" withBorder>
                  <Group position="apart">
                    <Group spacing="xs">
                      <IconGavel/>
                      <Title order={4}>
                        {t("page.settlement.title", "Settlement", {ns: "translation"})}
                      </Title>
                    </Group>
                    <Menu
                      width={260}
                      position="bottom-end"
                      transitionProps={{transition: "pop-top-right"}}
                      withinPortal
                    >
                      <Menu.Target>
                        <UnstyledButton>
                          <Group spacing={7}>
                            <Text weight={500} size="sm" sx={{lineHeight: 1}} mr={3}>
                              {t('common.action.plural', "Actions")}
                            </Text>
                            <IconChevronDown size={rem(12)} stroke={1.5}/>
                          </Group>
                        </UnstyledButton>
                      </Menu.Target>
                      <Menu.Dropdown>
                        <Menu.Item onClick={() => refetch()}>
                          {t("common.action.refresh", "Refresh", {ns: "common"})}
                        </Menu.Item>
                      </Menu.Dropdown>
                    </Menu>
                  </Group>

                  <Group position="center" spacing="xs">
                    <IconCalendar size="1.1rem" stroke={1.5}/>
                    <MonthPickerInput
                      defaultValue={new Date(filter.dttm)}
                      onChange={onFilterChange}
                      popoverProps={{
                        withinPortal: true
                      }}/>
                  </Group>
                </Card>
                <SettlementDataTable
                  withBorder
                  records={settlements}
                  recordsPerPageOptions={PAGINATION_PAGE_SIZES}
                  fetching={isFetching}/>
              </Stack>
            </Container>
          )}
        </ResizeableContent>
      )}
    </ThemedPage>
  );
};
